import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./SolariPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Solari" (/solari) — an EXPIRATION paywall, built to the standing #paywalls
// directive: SECURITY & PRIVACY.
//
// The mechanic no other paywall here uses: a tactile SPLIT-FLAP STATUS BOARD —
// the Solari departure boards from old train stations. Your Premium watch has
// lapsed, so every guard row reads OFF DUTY. On load (and whenever you flip the
// master switch) the board CLATTERS back to life in a top-to-bottom cascade,
// each protection flipping character-by-character from OFF DUTY to its live
// status — ENCRYPTED · SYNCED · ON WATCH · ARMED · READY · CLEAR — until the
// whole vault is back on watch over a filmstrip of your memories.
//
// This is deliberately NOT Halo's "draw a ring closed", not Locket's opening
// heirloom, not Lantern's dark-room-revealed-by-light, not a dial/keypad/door.
// The register is a mechanical status board coming back on duty — nostalgic,
// tactile, kinetic — with contrast framing (guard OFF vs ON) you can toggle
// yourself. Value framing is loss-aversion done HONESTLY: nothing was ever lost
// or exposed; the vault stays locked on the free plan too — only the PREMIUM
// guard rows were off duty, and this brings them back on watch.
//
// App-Store compliant: featured, most-prominent price is the YEARLY plan
// (7-day free trial, per-month broken out, honest Save %); monthly is a small
// secondary line. No scarcity / countdown, no fake urgency. Explicit no-data-
// loss reassurance — resuming Premium re-arms extra protection, it never
// recovers "lost" photos because nothing is ever deleted or locked away.
// Reuses only the house font, colour tokens and the bundled byheart memory
// photos. Full light + dark, reduced-motion aware. Renders standalone on debug
// data; no native injection.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// The memories on the board's marquee — reused bundled byheart photos, the only
// assets this paywall pulls in.
const MEMORIES = ["m03", "m07", "m12", "m05", "m14", "m09"].map(photo)

// Every status cell is this many character slots wide, so the whole board
// clatters in a clean grid.
const WIDTH = 9

// Center-pad a status word into a fixed-width cell.
function padWord(w: string) {
  const room = WIDTH - w.length
  if (room <= 0) return w.slice(0, WIDTH)
  const left = Math.floor(room / 2)
  return " ".repeat(left) + w + " ".repeat(room - left)
}

const OFF_WORD = padWord("OFF DUTY")

// Six REAL Premium protections, each with the live status it flips to.
type Guard = {
  id: string
  label: string
  hint: string
  on: string // live status word (padded below)
  icon: JSX.Element
}
const GUARDS: Guard[] = [
  {
    id: "backup",
    label: "Encrypted backup",
    hint: "A safe copy, kept",
    on: "ENCRYPTED",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M7 18a4 4 0 0 1-.6-7.96 5 5 0 0 1 9.7-1.2A3.8 3.8 0 0 1 18.5 18"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.4 13.6l1.9 1.9 3.5-3.7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "sync",
    label: "Cloud sync",
    hint: "Every device, in step",
    on: "SYNCED",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M5 12a7 7 0 0 1 11.9-5M19 12A7 7 0 0 1 7.1 17"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <path
          d="M16.4 3.6V7h-3.4M7.6 20.4V17h3.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "breakin",
    label: "Break-in alerts",
    hint: "Catch the snoop in the act",
    on: "ON WATCH",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M12 3.4l7 3v5c0 4.4-3 7.4-7 8.7-4-1.3-7-4.3-7-8.7v-5l7-3z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M12 8.4v4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
        <circle cx="12" cy="15.4" r="1.05" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: "lock",
    label: "Face ID / PIN lock",
    hint: "Only you get in",
    on: "ARMED",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M7.8 10V7.7a4.2 4.2 0 0 1 8.4 0V10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <rect
          x="5.4"
          y="10"
          width="13.2"
          height="9.2"
          rx="2.5"
          fill="currentColor"
        />
        <circle cx="12" cy="14.2" r="1.3" fill="var(--so-flap-face)" />
      </svg>
    ),
  },
  {
    id: "decoy",
    label: "Decoy vault",
    hint: "A false door",
    on: "READY",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <rect
          x="4.4"
          y="6.2"
          width="12"
          height="12"
          rx="2.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        />
        <rect
          x="8.4"
          y="3.2"
          width="11.2"
          height="11.2"
          rx="2.1"
          fill="var(--so-flap-face)"
          stroke="currentColor"
          strokeWidth="1.6"
        />
      </svg>
    ),
  },
  {
    id: "adfree",
    label: "Ad-free vault",
    hint: "Just your memories",
    on: "CLEAR",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <circle
          cx="12"
          cy="12"
          r="8.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M6.6 6.6l10.8 10.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
]

const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789★◆●".split("")

// ── One split-flap character cell ────────────────────────────────────────────
// On each play it spins through random glyphs then lands on its target char.
// Later columns spin a touch longer so a word resolves left-to-right, and each
// glyph change remounts a tiny leaf so the flap keyframe replays cleanly.
function Flap({
  target,
  playKey,
  col,
  reduce,
}: {
  target: string
  playKey: number
  col: number
  reduce: boolean | null
}) {
  const [char, setChar] = useState(target)
  const [nonce, setNonce] = useState(0)
  const timer = useRef<number | null>(null)

  useEffect(() => {
    if (timer.current != null) window.clearTimeout(timer.current)
    // First mount (playKey 0) and reduced-motion just show the final char.
    if (playKey === 0 || reduce) {
      setChar(target)
      return
    }
    const frames = 6 + col * 2 // later columns clatter a little longer
    let i = 0
    const tick = () => {
      if (i >= frames) {
        setChar(target)
        setNonce((n) => n + 1)
        return
      }
      // Nudge random glyphs toward the target as we near the end.
      const near = i > frames - 3
      setChar(
        near && target !== " " ? target : GLYPHS[(i * 7 + col) % GLYPHS.length],
      )
      setNonce((n) => n + 1)
      i += 1
      timer.current = window.setTimeout(tick, 58)
    }
    tick()
    return () => {
      if (timer.current != null) window.clearTimeout(timer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playKey])

  const blank = char === " " || char === ""
  return (
    <span className={styles.flap}>
      <span key={nonce} className={styles.leaf}>
        {blank ? " " : char}
      </span>
    </span>
  )
}

function FlapWord({
  word,
  playKey,
  reduce,
}: {
  word: string
  playKey: number
  reduce: boolean | null
}) {
  const chars = word.padEnd(WIDTH).slice(0, WIDTH).split("")
  return (
    <span className={styles.word} aria-hidden>
      {chars.map((c, i) => (
        <Flap key={i} target={c} playKey={playKey} col={i} reduce={reduce} />
      ))}
    </span>
  )
}

function SolariPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  const darkMode = state.initialData?.darkMode ?? false
  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  const numPhotos = state.productData?.numPhotos ?? 0
  const numAlbums = state.productData?.numAlbums ?? 0
  const libraryLine =
    numPhotos > 0
      ? `${numPhotos.toLocaleString()} memories · ${numAlbums} album${
          numAlbums === 1 ? "" : "s"
        }`
      : "Your memories · kept private"

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // The board's live/off-duty state, and a per-row play counter that retriggers
  // the clatter. armed=true means every guard is back on watch.
  const [armed, setArmed] = useState<boolean>(!!reduce)
  const [rowKeys, setRowKeys] = useState<number[]>(() => GUARDS.map(() => 0))

  const bumpAll = useCallback(() => {
    setRowKeys((keys) => keys.map((k) => k + 1))
  }, [])
  const bumpRow = useCallback((i: number) => {
    setRowKeys((keys) => keys.map((k, j) => (j === i ? k + 1 : k)))
  }, [])

  useEffect(() => {
    actions.setHeaderInsetColor(darkMode ? "#101218" : "#ece6da")
    actions.setFooterInsetColor(darkMode ? "#12141c" : "#fbf8f2")
  }, [actions, darkMode])

  // On load the board arrives OFF DUTY, then a beat later clatters back on watch
  // so the payoff reads within ~3s. Reduced motion arrives already armed.
  useEffect(() => {
    if (reduce) {
      setArmed(true)
      return
    }
    const t = window.setTimeout(() => {
      setArmed(true)
      bumpAll()
    }, 650)
    return () => window.clearTimeout(t)
  }, [reduce, bumpAll])

  const toggleArmed = () => {
    if (reduce) return
    setArmed((a) => !a)
    bumpAll()
  }

  const tapRow = (i: number) => {
    if (reduce) return
    bumpRow(i)
  }

  const liveCount = armed ? GUARDS.length : 0

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    native.logMixpanelEvent("html_solari_purchase", { plan })
    if (product) native.subscribe(product)
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  const ctaLabel =
    plan === "yearly" && yearly?.hasTrial
      ? "Start 7-day free trial"
      : "Bring my vault back on watch"

  const ctaSub =
    plan === "yearly"
      ? yearly?.hasTrial
        ? `Then ${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
        : `${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
      : `${monthlyPrice}/month. Cancel anytime.`

  return (
    <div className={`${styles.root} ${darkMode ? styles.isDark : ""}`}>
      <div className={styles.grain} aria-hidden />

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className={styles.scrim} style={{ paddingTop: topInset }}>
        <div className={styles.topbar}>
          <div className={styles.brand}>
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M8 10V7.7a4 4 0 0 1 8 0V10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
              <rect
                x="5.4"
                y="10"
                width="13.2"
                height="9.4"
                rx="2.6"
                fill="currentColor"
              />
              <circle cx="12" cy="14.2" r="1.35" fill="var(--so-brand-hole)" />
            </svg>
            <span>
              Private Photo Vault <b>Premium</b>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={handleClose}
            aria-label="Not now"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <header className={styles.hero}>
          <span className={styles.kicker}>
            <i className={styles.kickDot} data-armed={armed} />
            {armed
              ? "Your Premium watch is on again"
              : "Your Premium watch is off duty"}
          </span>
          <h1 className={styles.title}>Bring your vault back on watch.</h1>
          <p className={styles.sub}>
            Your memories stayed locked and yours the whole time — flip your
            Premium guard back on.
          </p>
        </header>
      </div>

      {/* ── The Solari status board ─────────────────────────────────────── */}
      <div className={styles.stage}>
        <div className={`${styles.board} ${armed ? styles.boardOn : ""}`}>
          <div className={styles.boardHead}>
            <div className={styles.marquee}>
              {MEMORIES.map((src, i) => (
                <span
                  key={src}
                  className={styles.frame}
                  style={{ backgroundImage: `url(${src})`, ["--i" as any]: i }}
                >
                  <span
                    className={styles.frameLock}
                    data-armed={armed}
                    aria-hidden
                  >
                    <svg viewBox="0 0 24 24" width="10" height="10">
                      <path
                        d="M8.5 10.5V8.6a3.5 3.5 0 0 1 7 0v1.9"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <rect
                        x="6.4"
                        y="10.4"
                        width="11.2"
                        height="8"
                        rx="2.2"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                </span>
              ))}
            </div>
            <div className={styles.marqueeMeta}>
              <span className={styles.metaTag}>PRIVATE</span>
              <span className={styles.metaCount}>{libraryLine}</span>
              <button
                className={styles.master}
                onClick={toggleArmed}
                role="switch"
                aria-checked={armed}
                aria-label="Guard on watch"
              >
                <span className={styles.masterLabel}>
                  {armed ? "ON" : "OFF"}
                </span>
                <span className={styles.masterTrack} data-on={armed}>
                  <span className={styles.masterKnob} />
                </span>
              </button>
            </div>
          </div>

          <div className={styles.rows}>
            {GUARDS.map((g, i) => {
              const target = armed ? padWord(g.on) : OFF_WORD
              return (
                <button
                  key={g.id}
                  type="button"
                  className={`${styles.row} ${armed ? styles.rowOn : ""}`}
                  onClick={() => tapRow(i)}
                  title={g.hint}
                >
                  <span className={styles.rowIcon}>{g.icon}</span>
                  <span className={styles.rowLabel}>
                    <span className={styles.rowName}>{g.label}</span>
                    <span className={styles.rowHint}>{g.hint}</span>
                  </span>
                  <span className={styles.cell} data-on={armed}>
                    <FlapWord
                      word={target}
                      playKey={rowKeys[i]}
                      reduce={reduce}
                    />
                  </span>
                  <span className={styles.lamp} data-on={armed} aria-hidden />
                </button>
              )
            })}
          </div>

          <div className={styles.boardFoot} aria-live="polite">
            <span className={styles.footDot} data-on={armed} />
            {armed
              ? `${liveCount} of ${GUARDS.length} guards on watch`
              : "Guards off duty — flip the switch"}
          </div>
        </div>
      </div>

      {/* ── Pricing + CTA (yearly featured) ─────────────────────────────── */}
      <div className={styles.panel} style={{ paddingBottom: bottomInset + 20 }}>
        <div className={styles.plans}>
          <button
            className={`${styles.planYear} ${plan === "yearly" ? styles.planOn : ""}`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>Save {savings}%</span>
            )}
            <span className={styles.radio} data-on={plan === "yearly"} />
            <span className={styles.planCopy}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                {yearly?.hasTrial
                  ? "7-day free trial, then billed yearly"
                  : "billed yearly"}
              </span>
            </span>
            <span className={styles.priceCol}>
              <span className={styles.priceRow}>
                <b className={styles.priceBig}>{yearlyPrice}</b>
                <span className={styles.pricePer}>/yr</span>
              </span>
              <span className={styles.pricePm}>{perMonth}/mo</span>
            </span>
          </button>

          <button
            className={`${styles.planMonth} ${plan === "monthly" ? styles.planOn : ""}`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.radio} data-on={plan === "monthly"} />
            <span className={styles.planNameSm}>Monthly</span>
            <span className={styles.priceSm}>
              {monthlyPrice}
              <span className={styles.pricePerSm}>/mo</span>
            </span>
          </button>
        </div>

        <button className={styles.cta} onClick={purchase}>
          <span>{ctaLabel}</span>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
            <path
              d="M5 12h13M13 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <p className={styles.ctaSub}>{ctaSub}</p>
        <p className={styles.reassure}>
          Your vault stays locked and private on the free plan too — nothing is
          ever deleted or locked away, and you always hold the only key.
          Resuming Premium simply brings the extra guard rows back on watch.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default SolariPaywall
